import { useEffect, useRef } from "react";

/**
 * Re-fetches data when the browser tab becomes visible after being hidden
 * for longer than the stale threshold.
 *
 * This is a belt-and-suspenders fallback for SignalR-based real-time updates.
 * Even if the SignalR connection was lost while the tab was hidden (laptop
 * suspended, network issue, etc.), this hook ensures data is refreshed.
 *
 * @param callback - Function to call when tab becomes visible and data is stale
 * @param staleAfterMs - Milliseconds before data is considered stale (default: 60000 = 1 minute)
 *
 * Usage:
 *   useVisibilityRefresh(() => {
 *     dispatch(getPublicServiceRequests(query));
 *   }, 60000);
 */
const useVisibilityRefresh = (
  callback: () => void,
  staleAfterMs: number = 60000
) => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const hiddenAtRef = useRef<number | null>(null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab became hidden — record timestamp
        hiddenAtRef.current = Date.now();
      } else {
        // Tab became visible — check if stale
        if (hiddenAtRef.current !== null) {
          const elapsed = Date.now() - hiddenAtRef.current;
          if (elapsed >= staleAfterMs) {
            callbackRef.current();
          }
          hiddenAtRef.current = null;
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [staleAfterMs]);
};

export default useVisibilityRefresh;
